import forge from 'apis/forge';
import QualityToggle from 'components/Panel/Chat/ChatInput/SceneChatInput/QualityToggle';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import ActionButton from 'components/Widget/Button/ActionButton';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import DsPicker from 'components/Widget/DsPicker';
import { Attachment, DesignSystem, DesignSystemVersion } from 'dragonmouse-shared/interfaces';
import { CurrencyLevel, interceptSubmissionIfWalletEmpty } from 'economy';
import { MenuItemAction } from 'menu/MenuItem';
import { useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { showContextMenu, showGoldRefillModal } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { selectDesignSystems, selectUserSettings, selectWalletLevels } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import { useNavigate } from 'react-router';
import { useTheme } from 'styled-components';
import * as routes from 'routes';
import { FileUploadCallbacks, onFilePasted, onUploadFileDialog, UploadTracker } from 'utils';
import { PROMPT_EXAMPLES, DESIGN_SYSTEM_PROMPT_EXAMPLES } from './constants';
import * as S from './styles';

interface CreatorInputConfig {
	submitButtonText: string;
	placeholder: string;
	showQualityToggle: boolean;
}

const MODE_CONFIG: Record<'project' | 'designSystem', CreatorInputConfig> = {
	project: {
		submitButtonText: 'Create Design',
		placeholder: 'Describe your design idea...',
		showQualityToggle: true,
	},
	designSystem: {
		submitButtonText: 'Create Design System',
		placeholder: 'Describe your design system, or upload images...',
		showQualityToggle: false,
	},
};

interface CreatorInputProps {
	onSubmit: (prompt: string, attachments: Attachment[]) => Promise<void>;
	disabled?: boolean;
	mode: 'project' | 'designSystem';
	onDesignSystemSelect?: (designSystemId: string | null) => void;
	onCreateBlank?: () => Promise<void>;
}

const CreatorInput = ({
	onSubmit,
	disabled = false,
	mode,
	onDesignSystemSelect,
	onCreateBlank,
}: CreatorInputProps) => {
	const config = MODE_CONFIG[mode];
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const theme = useTheme();
	const { isMobile, designSystems, designSystemsEnabled, walletLevels } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			designSystems: selectDesignSystems(r.db),
			designSystemsEnabled: selectUserSettings(r.db).designSystemsEnabled,
			walletLevels: selectWalletLevels(r.db),
		}),
		shallowEqual
	);
	const goldLevel: CurrencyLevel | undefined = walletLevels?.goldLevel;

	const [prompt, setPrompt] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [loadingAttachments, setLoadingAttachments] = useState<UploadTracker[]>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [undoStack, setUndoStack] = useState<string[]>([]);
	const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
	const [selectedDesignSystemId, setSelectedDesignSystemId] = useState<string | null>(null);
	const [selectedDsVersion, setSelectedDsVersion] = useState<DesignSystemVersion | null>(null);
	const [selectedDsLatestPublishedVersionNumber, setSelectedDsLatestPublishedVersionNumber] =
		useState<number | null>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	// Validation logic for whether input can be submitted
	const hasLoadingAttachments = useMemo(
		() => loadingAttachments && loadingAttachments.length > 0,
		[loadingAttachments]
	);
	const minimalInputEntered = useMemo(
		() => prompt.length > 0 || attachments.length > 0,
		[prompt, attachments]
	);
	// Allow submission when empty if onCreateBlank is provided (for blank project creation)
	const canSubmitBase =
		(minimalInputEntered || (mode === 'project' && onCreateBlank)) &&
		!hasLoadingAttachments &&
		!disabled &&
		!isSubmitting;
	const canSubmit = canSubmitBase && (mode !== 'designSystem' || goldLevel !== 'EMPTY');

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const handleSubmit = async () => {
		if (!canSubmit) return;

		// Set submitting state immediately to disable button and prevent multiple submissions
		setIsSubmitting(true);

		// If no input and onCreateBlank is provided, create blank project
		if (!minimalInputEntered && mode === 'project' && onCreateBlank) {
			try {
				await onCreateBlank();
			} catch (error) {
				// Error handling is done in parent component
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		// Refresh wallet to ensure we have the latest balance before checking
		try {
			await forge.fetchWallet();
			// Get updated wallet levels after refresh
			const state = store.getState();
			const updatedWalletLevels = selectWalletLevels(state.db);
			if (mode === 'designSystem' && updatedWalletLevels?.goldLevel === 'EMPTY') {
				dispatch(showGoldRefillModal({ refillsAvailable: true }));
				setIsSubmitting(false);
				return;
			}
		} catch (error) {
			console.error('Failed to refresh wallet before submission:', error);
			// Check cached wallet data if refresh failed
			if (mode === 'designSystem' && goldLevel === 'EMPTY') {
				dispatch(showGoldRefillModal({ refillsAvailable: true }));
				setIsSubmitting(false);
				return;
			}
		}

		if (interceptSubmissionIfWalletEmpty()) {
			setIsSubmitting(false);
			return;
		}

		try {
			await onSubmit(prompt, attachments);
			// Clear state after successful submission
			setPrompt('');
			setAttachments([]);
		} catch (error) {
			// Error handling is done in parent component
		} finally {
			setIsSubmitting(false);
		}
	};

	const onChangePrompt = (e: any) => {
		setPrompt(e.target.value);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		// Handle Ctrl+Z/Cmd+Z for undo
		if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
			if (undoStack.length > 0) {
				e.preventDefault();
				const previousPrompt = undoStack[undoStack.length - 1];
				setUndoStack(undoStack.slice(0, -1));
				setPrompt(previousPrompt);
				// Focus input after undo
				setTimeout(() => {
					inputRef.current?.focus();
				}, 0);
			}
			return;
		}

		// On desktop: Enter submits, Shift+Enter adds line break
		// On mobile: Enter adds line break, user taps button to submit
		if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
			e.preventDefault();
			if (canSubmit) {
				handleSubmit();
			}
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		setAttachments(attachments.filter((a) => a.storagePath !== storagePath));
	};

	const handlePaste = (text: string) => {
		// Insert the pasted text at the current cursor position in the prompt
		const textarea = inputRef.current;
		if (textarea) {
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const newPrompt = prompt.substring(0, start) + text + prompt.substring(end);
			setPrompt(newPrompt);

			// Focus the input after pasting
			setTimeout(() => {
				textarea.focus();
				textarea.setSelectionRange(start + text.length, start + text.length);
			}, 0);
		} else {
			// Fallback if textarea ref is not available
			setPrompt(prompt + text);
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	};

	const onContextMenu = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (e.target instanceof HTMLElement) {
			const paste = await MenuItemAction.createPasteText(handlePaste);
			const menuItems = [paste];
			const contextMenuAction = showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems,
			});
			dispatch(contextMenuAction);
		}
	};

	// File upload callbacks with functional setState to avoid stale closures
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				setLoadingAttachments((prev) => [...prev, uploadTracker]);
			},
			onLoadingEnd: (uniqueId: string) => {
				setLoadingAttachments((prev) => prev.filter((a) => a.uniqueId !== uniqueId));
			},
			onSuccess: (attachment: Attachment) => {
				setAttachments((prev) => {
					// Check if file already exists
					if (prev.some((a) => a.storagePath === attachment.storagePath)) {
						toast.warning('This file is already attached');
						return prev;
					}
					return [...prev, attachment];
				});
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[]
	);

	const handleTryExample = () => {
		// Save current prompt to undo stack if it exists
		if (prompt.trim().length > 0) {
			setUndoStack([...undoStack, prompt]);
		}

		// Select examples based on mode
		const examples = mode === 'designSystem' ? DESIGN_SYSTEM_PROMPT_EXAMPLES : PROMPT_EXAMPLES;

		// Select a random prompt, avoiding the last one used
		let randomIndex;
		do {
			randomIndex = Math.floor(Math.random() * examples.length);
		} while (randomIndex === lastSelectedIndex && examples.length > 1);

		setLastSelectedIndex(randomIndex);
		setPrompt(examples[randomIndex]);

		// Focus input after setting example
		setTimeout(() => {
			inputRef.current?.focus();
		}, 0);
	};

	const handleDesignSystemSelect = async (designSystem: DesignSystem | null) => {
		const dsId = designSystem?.id ?? null;
		setSelectedDesignSystemId(dsId);

		// Clear version info when deselecting
		if (!dsId) {
			setSelectedDsVersion(null);
			setSelectedDsLatestPublishedVersionNumber(null);
			if (onDesignSystemSelect) {
				onDesignSystemSelect(null);
			}
			return;
		}

		// Fetch version info for the selected design system
		try {
			const versionInfo = await forge.getDesignSystemVersionInfo(dsId);
			setSelectedDsVersion(versionInfo.version);
			setSelectedDsLatestPublishedVersionNumber(versionInfo.latestPublishedVersionNumber);

			// Check if DS has a published version
			if (!versionInfo.latestPublishedVersionNumber) {
				toast.warning(
					'This design system has no published version yet. If you just created it, please wait a couple of minutes and try again.'
				);
				setSelectedDesignSystemId(null);
				setSelectedDsVersion(null);
				setSelectedDsLatestPublishedVersionNumber(null);
				onDesignSystemSelect?.(null);
				return;
			}

			// Check if the version is ready for use (prompt generation complete)
			const promptStatus = versionInfo.version?.manifest?.promptGenerationStatus;
			if (promptStatus !== 'complete') {
				if (promptStatus === 'pending' || promptStatus === 'generating') {
					toast.warning(
						'This design system is not ready for use yet. Please wait a couple of minutes and try again.'
					);
				} else if (promptStatus === 'failed') {
					toast.warning('This design system failed to publish. Please try publishing again.');
				} else {
					toast.warning(
						'This design system is not ready for use yet. Please wait a couple of minutes and try again.'
					);
				}
				setSelectedDesignSystemId(null);
				setSelectedDsVersion(null);
				setSelectedDsLatestPublishedVersionNumber(null);
				onDesignSystemSelect?.(null);
				return;
			}

			// DS is valid - notify parent
			if (onDesignSystemSelect) {
				onDesignSystemSelect(dsId);
			}
		} catch (error) {
			console.error('Failed to fetch design system version info:', error);
			setSelectedDesignSystemId(null);
			setSelectedDsVersion(null);
			setSelectedDsLatestPublishedVersionNumber(null);
			onDesignSystemSelect?.(null);
		}
	};

	const handleCreateNewDesignSystem = () => {
		navigate(routes.createDesignSystem());
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderAttachments = () => {
		const allAttachments = [
			...attachments,
			...loadingAttachments.map((attachment) => ({
				...attachment,
				storagePath: `loading-${attachment.uniqueId}`, // Use uniqueId for loading attachments
			})),
		];

		if (!allAttachments.length) return null;
		return (
			<S.Attachments
				onWheel={(e) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{allAttachments.map((attachment) => {
					const isLoading = loadingAttachments.some(
						(a) => a.uniqueId === attachment.storagePath.replace('loading-', '')
					);
					return (
						<ChatAttachmentTile
							key={attachment.storagePath}
							attachment={attachment}
							onDelete={isLoading ? undefined : () => onRemoveAttachment(attachment.storagePath)}
							state={isLoading ? ChatAttachmentTileState.LOADING : ChatAttachmentTileState.REGULAR}
						/>
					);
				})}
			</S.Attachments>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<ActionButton
				icon={IconType.ADD}
				tooltip="Upload attachment"
				onClick={() => onUploadFileDialog(fileCallbacks)}
			/>
		);
	};

	const renderGoldIndicator = () => {
		if (mode !== 'designSystem') return null;

		const tooltipId = 'ds-creator-gold-indicator-tooltip';
		let tooltipContent = 'Design System edits require gold.';
		if (goldLevel === 'LOW') tooltipContent += '<br><br>⚠️ Gold is low.';
		else if (goldLevel === 'EMPTY') tooltipContent += '<br><br>⚠️ You are out of gold.';
		else tooltipContent += '<br><br>You have ample gold available.';

		// Wrap content in div with inline styles to ensure proper wrapping
		tooltipContent = `<div style="max-width: 240px; line-height: 1.6; word-wrap: break-word; white-space: normal; overflow-wrap: break-word;">${tooltipContent}</div>`;

		return (
			<S.GoldIndicatorWrapper data-tooltip-id={tooltipId} data-tooltip-html={tooltipContent}>
				<S.GoldCircle walletLevel={goldLevel}>
					{goldLevel === 'LOW' && (
						<S.ErrorIconOverlay>
							<Icon type={IconType.BANG} width={15} height={15} color={theme.gray[100]} />
						</S.ErrorIconOverlay>
					)}
				</S.GoldCircle>
				<Tooltip id={tooltipId} placement="top" />
			</S.GoldIndicatorWrapper>
		);
	};

	const renderActions = () => {
		return (
			<S.Actions isMobile={isMobile}>
				{!isMobile && renderAttachmentButton()}
				{mode === 'project' && designSystemsEnabled && (
					<DsPicker
						selection={
							selectedDesignSystemId
								? {
										id: selectedDesignSystemId,
										status: (() => {
											// Compute status based on version info
											if (!selectedDsLatestPublishedVersionNumber) return null;
											const promptStatus = selectedDsVersion?.manifest?.promptGenerationStatus;
											if (promptStatus === 'pending' || promptStatus === 'generating') {
												return 'publishing';
											}
											return 'latest';
										})(),
										// In project creator context, linkedVersion = latestVersion
										// since we'll link to the latest published when scene is created
										linkedVersion: selectedDsVersion,
										latestVersion: selectedDsVersion,
								  }
								: null
						}
						designSystems={designSystems}
						onSelect={handleDesignSystemSelect}
						onCreateNew={handleCreateNewDesignSystem}
					/>
				)}
				<S.Spacer />
				{renderGoldIndicator()}
				{config.showQualityToggle && <QualityToggle />}
				{isSubmitting ? (
					<S.LoadingWrapper isMobile={isMobile}>
						<Loader width={isMobile ? 32 : 22} height={isMobile ? 32 : 22} />
					</S.LoadingWrapper>
				) : (
					<S.SubmitButtonWrapper
						isMobile={isMobile}
						isDisabled={!canSubmit}
						onClick={canSubmit ? handleSubmit : undefined}
						disabled={!canSubmit}
					>
						<S.SubmitButtonIcon>
							<Icon type={IconType.LINE_ARROW_UP} width={16} height={16} color="#FFFFFF" />
						</S.SubmitButtonIcon>
						<S.SubmitButtonText>
							{mode === 'project' && !minimalInputEntered && onCreateBlank
								? 'Create Blank Project'
								: config.submitButtonText}
						</S.SubmitButtonText>
					</S.SubmitButtonWrapper>
				)}
			</S.Actions>
		);
	};

	return (
		<S.Base isMobile={isMobile}>
			<S.Prompt isFocused={isFocused}>
				{renderAttachments()}
				<S.MultilineInputWrapper isMobile={isMobile} onPaste={onFilePasted(fileCallbacks)}>
					<MultilineInput
						ref={inputRef}
						value={prompt}
						onInput={onChangePrompt}
						onKeyDown={onKeyDown}
						placeholder={config.placeholder}
						minLines={2}
						renderBorder={false}
						variant="large"
						onFocusChange={(isFocused) => {
							setIsFocused(isFocused);
						}}
						onContextMenu={onContextMenu}
					/>
				</S.MultilineInputWrapper>
				{renderActions()}
			</S.Prompt>
			<S.ExamplePromptLink onClick={handleTryExample}>Try an example</S.ExamplePromptLink>
		</S.Base>
	);
};

export default CreatorInput;
